import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { ArrowRight, CheckCircle, AlertCircle } from 'lucide-react';
import { useBrand } from '../context/BrandContext';
import useAntiBot from '../hooks/useAntiBot';
import useRecaptcha from '../hooks/useRecaptcha';
import { trackEvent } from '../utils/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (value) => {
  // Strip non-numeric characters
  const numbers = value.replace(/[^0-9]/g, '');
  if (!numbers) return '';
  // Format with commas
  return '$' + Number(numbers).toLocaleString('en-AU');
};

const loanTypes = [
  'Home Loan',
  'Refinance',
  'Investment Property',
  'Commercial Loan',
  'Car Loan',
  'Asset Finance',
  'Personal Loan',
];

// Shown only for Refinance leads. Labels are VERBATIM — CRMandGo routes off these
// values (asset types → Add Finance CRM; Home loan / Investment property → MYL). Do not reword.
const refinancingOptions = [
  'Home loan',
  'Investment property',
  'Car or vehicle',
  'Business or equipment',
  'Personal loan or debt',
  'Boat, caravan or other',
];

const auStates = ['NSW', 'VIC', 'QLD', 'WA', 'SA', 'TAS', 'ACT', 'NT'];

const ContactForm = ({ pageContext = '' }) => {
  const brand = useBrand();
  const source = brand.source;
  const showCostcoFields = brand.showCostcoFields;
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    loanType: pageContext || '',
    loanAmount: '',
    costcoMembershipNumber: '',
    message: '',
    refinancing: '',
    state: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const { honeypotProps, getAntiBotPayload, validateSubmission } = useAntiBot();
  const { getRecaptchaToken } = useRecaptcha();
  const sectionRef = useRef(null);
  const resetTimerRef = useRef(null);

  // Don't leave a timer running against an unmounted form.
  useEffect(() => () => clearTimeout(resetTimerRef.current), []);

  // The thank-you panel is much shorter than the form it replaces, so the page
  // collapses while the browser keeps the old scroll offset — leaving the
  // confirmation off-screen. Pull it into view so it's actually seen.
  useEffect(() => {
    if (submitted) {
      sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [submitted]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'loanAmount') {
      setFormData((prev) => ({ ...prev, [name]: formatCurrency(value) }));
    } else if (name === 'phone') {
      const digits = value.replace(/[^0-9]/g, '');
      setFormData((prev) => ({ ...prev, [name]: digits }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const botCheck = validateSubmission();
    if (!botCheck.ok) {
      if (botCheck.silent) {
        // Fake success for bots — don't reveal detection
        setSubmitted(true);
        return;
      }
      setError(botCheck.error);
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const recaptchaToken = await getRecaptchaToken('submit_lead');

      const payload = {
        ...formData,
        source,
        ...getAntiBotPayload(),
        recaptchaToken,
      };
      // Remove empty optional fields
      if (!payload.costcoMembershipNumber) delete payload.costcoMembershipNumber;
      if (!payload.loanAmount) delete payload.loanAmount;
      if (!payload.message) delete payload.message;
      if (!payload.state) delete payload.state;
      // `refinancing` only applies to refinance leads — never send a stale value from a
      // prior selection if the user changed the loan type away from Refinance.
      if (payload.loanType !== 'Refinance' || !payload.refinancing) delete payload.refinancing;

      const response = await axios.post(`${BACKEND_URL}/api/leads`, payload);

      if (response.data.status === 'success') {
        trackEvent('form_submit', { form_type: 'lead', source });
        setSubmitted(true);
        resetTimerRef.current = setTimeout(() => {
          // Return the visitor to the top of the page rather than snapping the
          // form back under them, then restore a clean form behind them.
          window.scrollTo({ top: 0, behavior: 'smooth' });
          setSubmitted(false);
          setFormData({
            firstName: '',
            lastName: '',
            phone: '',
            email: '',
            loanType: pageContext || '',
            loanAmount: '',
            costcoMembershipNumber: '',
            message: '',
            refinancing: '',
            state: '',
          });
        }, 5000);
      }
    } catch (err) {
      console.error('Lead submission failed:', err);
      const detail = err.response?.data?.detail;
      const message = typeof detail === 'string' ? detail : 'Something went wrong. Please try again or call us on 0468 007 056.';
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <section ref={sectionRef} id="book-appointment" className="py-16 bg-gray-50 scroll-mt-20">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <div className="bg-white rounded-2xl p-12 shadow-sm">
            <div className="w-16 h-16 bg-[#99DF63]/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-[#00594C]" />
            </div>
            <h3 className="text-2xl font-semibold text-[#00594C] mb-4">
              Thank you for your enquiry!
            </h3>
            <p className="text-gray-600">
              Thanks for your enquiry, one of our Brokers will be in touch soon.
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="book-appointment" className="py-16 bg-gray-50 scroll-mt-20">
      <div className="max-w-2xl mx-auto px-4">
        <div className="text-center mb-10">
          <h2 className="text-2xl md:text-3xl font-semibold text-[#00594C] mb-4">
            {brand.formHeading}
          </h2>
          <p className="text-gray-600">
            {brand.formSubheading}
          </p>
        </div>

        <form onSubmit={handleSubmit} data-no-track className="bg-white rounded-2xl p-8 shadow-sm space-y-6">
          {honeypotProps.field}
          {/* Name Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#99DF63] focus:border-[#00594C] outline-none transition-colors"
                placeholder="John"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#99DF63] focus:border-[#00594C] outline-none transition-colors"
                placeholder="Smith"
              />
            </div>
          </div>

          {/* Contact Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#99DF63] focus:border-[#00594C] outline-none transition-colors"
                placeholder="john.smith@email.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                pattern="04[0-9]{8}"
                title="Australian mobile number starting with 04"
                maxLength={10}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#99DF63] focus:border-[#00594C] outline-none transition-colors"
                placeholder="04XX XXX XXX"
              />
            </div>
          </div>

          {/* Costco Membership (conditional) */}
          {showCostcoFields && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Costco Executive Membership Number *
              </label>
              <input
                type="text"
                name="costcoMembershipNumber"
                value={formData.costcoMembershipNumber}
                onChange={handleChange}
                required={showCostcoFields}
                pattern="[0-9]*"
                inputMode="numeric"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#99DF63] focus:border-[#00594C] outline-none transition-colors"
                placeholder="Enter your membership number"
              />
            </div>
          )}

          {/* Loan Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Loan Type *</label>
            <select
              name="loanType"
              value={formData.loanType}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#99DF63] focus:border-[#00594C] outline-none transition-colors bg-white"
            >
              <option value="">Select loan type</option>
              {loanTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>

          {/* What are you refinancing? — only for refinance leads, directly after Loan Type */}
          {formData.loanType === 'Refinance' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                What are you refinancing?
              </label>
              <select
                name="refinancing"
                value={formData.refinancing}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#99DF63] focus:border-[#00594C] outline-none transition-colors bg-white"
              >
                <option value="">Select what you're refinancing</option>
                {refinancingOptions.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Loan Amount & State */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Estimated Loan Amount
              </label>
              <input
                type="text"
                name="loanAmount"
                value={formData.loanAmount}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#99DF63] focus:border-[#00594C] outline-none transition-colors"
                placeholder="$500,000"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">What state are you in?</label>
              <select
                name="state"
                value={formData.state}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#99DF63] focus:border-[#00594C] outline-none transition-colors bg-white"
              >
                <option value="">Select your state</option>
                {auStates.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Message */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Message (Optional)</label>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              rows={4}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#99DF63] focus:border-[#00594C] outline-none transition-colors resize-none"
              placeholder="Tell us about your loan requirements..."
            />
          </div>

          {/* Error */}
          {error && (
            <div className="flex items-center space-x-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full flex items-center justify-center space-x-2 bg-[#99DF63] text-[#00594C] px-8 py-4 rounded-full font-bold text-lg hover:bg-[#8BCF52] transition-all shadow-lg hover:shadow-xl transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
          >
            <span>{isSubmitting ? 'Submitting...' : 'Submit Enquiry'}</span>
            {!isSubmitting && <ArrowRight className="w-5 h-5" />}
          </button>

          {/* Privacy */}
          <p className="text-center text-gray-500 text-sm">
            By submitting this form, you agree to our{' '}
            <a href="/privacy-policy" className="text-[#00594C] hover:underline">Privacy Policy</a> and{' '}
            <a href="/terms-of-use" className="text-[#00594C] hover:underline">Terms of Use</a>.
            {showCostcoFields && (
              <>
                {' '}See{' '}
                <a href="/costco-terms" className="text-[#00594C] hover:underline">
                  Costco Promotion Terms & Conditions
                </a>.
              </>
            )}
          </p>

          <p className="text-center text-gray-500 text-sm">
            Or call us directly at{' '}
            <a href="tel:+61468007056" className="text-[#00594C] font-semibold hover:underline">
              0468 007 056
            </a>
          </p>
        </form>
      </div>
    </section>
  );
};

export default ContactForm;
